/**
 * The god's reach — how far this deity's attention extends.
 *
 * One number does three jobs, and that is the point. Reach sets how high the
 * presence may rise, how fast it crosses the world (flight speed is granted
 * by altitude, and altitude ends here), and the widest Focus the wheel may
 * choose — main.ts caps the chosen radius at `focusFor(maxAltitude)`, the
 * exact focus the old orbit ceiling permitted. A spirit tending one hillside
 * cannot mean a valley: not because a rule forbids it, but because neither
 * its sky nor its attention stretches that far. Crossing a mountain by flying
 * high does NOT widen a cast — scale is chosen, never inferred from where
 * the camera happens to be.
 *
 * That makes progression physical rather than a menu of unlocked powers,
 * which is what the design has always wanted: influence widens, and the
 * widening is something you can feel in flight.
 *
 * No Three.js, no clock, no camera reads: this is state, and rendering asks
 * it questions.
 */

/** The stages of a god's arc (design doc §11). Names are what mortals mean. */
export type GodStage = 'spirit' | 'local' | 'regional' | 'old';

export interface GodStageInfo {
  stage: GodStage;
  /** What the stage is called when shown to the player. */
  title: string;
  /** How high the presence may rise, in metres over the land beneath it. */
  altitude: number;
  /** The scale of land this reach corresponds to, for the HUD. */
  scope: string;
}

/**
 * The ladder. Altitudes are chosen against the focus bands so that each stage
 * unlocks roughly one more register of divine action:
 *
 *   spirit    260 m  → presence and local casts: a tree, a camp, a work site
 *   local     900 m  → grove: a woodland, a herd, a settlement's ground
 *   regional 3.2 km  → valley: broad moisture, fire risk, whole hillsides
 *   old       60 km  → watershed and the whole island; nothing is out of reach
 */
const LADDER: readonly GodStageInfo[] = [
  // 260 → 180 (2026-08-18, Griffin: "reduce the height cap for level 1
  // deities"): a spirit stands off its hillside no further than it could
  // walk to in a minute
  { stage: 'spirit',   title: 'spirit',        altitude: 180,   scope: 'a hillside' },
  { stage: 'local',    title: 'local god',     altitude: 900,   scope: 'a valley' },
  { stage: 'regional', title: 'regional god',  altitude: 3200,  scope: 'a watershed' },
  { stage: 'old',      title: 'old god',       altitude: 60000, scope: 'the region' },
];

/** Player or rival. The world tells them apart; nothing in it does. */
export type DeityKind = 'player' | 'npc';

/**
 * ONE STONE OF IMPULSE — a fifth of the whole god (2026-08-18, Griffin's
 * direction). The bar shows a god's `power` as five stones and never a
 * number (`DECISIONS.md`, the chrome), and from today a cast SPENDS stones:
 * a tap of a verb's key spends one, a held key charges up to everything the
 * god has left. `hud.ts`'s PIPS is 5 for the same reason this is 0.2; the two
 * must agree, and this is the number the simulation prices in.
 */
export const IMPULSE_STONE = 0.2;

/**
 * THE ARC IS PAID FOR IN STONES (2026-08-18, Griffin's direction). A god's
 * level is how much impulse it has ever put into the world — a charged cast
 * counts for every stone it spent — and each level is the same number of
 * stones. Twelve is ~12 taps or two or three full charges: at the regeneration
 * above, about an hour of casting a level. Twelve levels walk the whole ladder
 * (`reachOf`), three a stage, so a spirit is a spirit for its first three
 * levels and an old god from its tenth. Design numbers, written to be measured
 * against play. Law 25's "a god learns from history, not button presses" is
 * superseded for the LEVEL by this ruling (`DECISIONS.md`); the Chronicle's
 * standing (`godhood/level.ts`) is still read and still counts — the two are
 * combined at the Web (`main.ts` `refreshLevel`).
 */
export const STONES_PER_LEVEL = 12;
/** Levels from the first stage to the top of the ladder. */
export const LEVELS_TO_OLD = 12;
/** Level from cumulative stones spent: 1 at nothing, +1 every STONES_PER_LEVEL. */
export const levelOf = (spentStones: number): number =>
  1 + Math.floor(Math.max(0, spentStones) / STONES_PER_LEVEL + 1e-9);
/** Reach (0..1 along the ladder) that a level has earned. */
export const reachOf = (level: number): number =>
  clamp01((Math.max(1, level) - 1) / LEVELS_TO_OLD);

/** A deity's seat in the world — where its attention lives. */
export interface DeitySeat { x: number; z: number }

export interface DeitySnapshot {
  id: string;
  kind: DeityKind;
  reach: number;
  power: number;
  impulse: number;
  /** Stones of impulse ever spent — the arc's currency. Absent in older saves: 0. */
  spent?: number;
  seat?: DeitySeat;
}

export class GodState {
  /**
   * Who this is. Divine events carry it as their true cause — the world's
   * own bookkeeping, never anything a mortal can read (see perceivedCause
   * in events.ts, and the wall test in test/deity.ts).
   */
  readonly id: string;
  readonly kind: DeityKind;
  /**
   * 0..1 — how far along the arc this god is. Devotion will drive this; until
   * then it is set directly, which is also what makes it testable.
   */
  reach: number;
  /**
   * 0..1 — capacity to act. Reach is how far a god's attention carries;
   * power is how much it can spend when it gets there. The player's is
   * unspent for now; an npc's will meter its interventions.
   */
  power: number;
  /**
   * 0..1 — how readily this god acts at all. A patient god watches for
   * seasons; a capricious one cannot let a week pass. Only npcs read it —
   * the player's impulse is the player.
   */
  impulse: number;
  /**
   * Stones of impulse this god has ever put into the world (`IMPULSE_STONE`
   * units, fractional for the old-quote casts a rival makes). Never
   * decreases. `levelOf(spent)` is the level; `reachOf(level)` is the least
   * reach that level has earned, and `Simulation.act` raises `reach` to it.
   */
  spent: number;
  /**
   * Where this god's attention lives, when it lives anywhere. A rival's seat
   * is *never* revealed: it exists so that its acts cluster somewhere real,
   * which is the only way the player can ever infer it — by travelling.
   */
  seat?: DeitySeat;

  constructor(
    reach = 0,
    opts: { id?: string; kind?: DeityKind; power?: number; impulse?: number;
            spent?: number; seat?: DeitySeat } = {},
  ) {
    this.reach = clamp01(reach);
    this.id = opts.id ?? 'player';
    this.kind = opts.kind ?? 'player';
    this.power = clamp01(opts.power ?? 1);
    this.impulse = clamp01(opts.impulse ?? 0.5);
    this.spent = Math.max(0, opts.spent ?? 0);
    if (opts.seat) this.seat = { ...opts.seat };
  }

  /** The level the stones spent have earned. */
  get level(): number { return levelOf(this.spent); }

  /**
   * Record impulse put into the world, and raise the arc if it has been earned.
   * `reach` only ever rises here — a dev key or a save may have set it higher,
   * and a level never takes back what a god already has.
   */
  spend(stones: number): void {
    this.spent += Math.max(0, stones);
    this.reach = Math.max(this.reach, reachOf(this.level));
  }

  /** Which rung the god currently stands on. */
  get stageInfo(): GodStageInfo {
    const i = Math.min(LADDER.length - 1, Math.floor(this.reach * LADDER.length));
    return LADDER[i];
  }

  get stage(): GodStage {
    return this.stageInfo.stage;
  }

  /**
   * The ceiling, in camera orbit metres. Interpolated *within* a rung as well
   * as between them, so growing reach opens the sky continuously instead of
   * jumping — a god that has almost earned the next stage can already see a
   * little further than one that just arrived at this one.
   */
  get maxAltitude(): number {
    const n = LADDER.length;
    const f = clamp01(this.reach) * n;
    const i = Math.min(n - 1, Math.floor(f));
    const within = f - i;
    const here = LADDER[i].altitude;
    const next = i + 1 < n ? LADDER[i + 1].altitude : here;
    // geometric, because altitude is perceived in ratios, not metres
    return here * Math.pow(next / here, within);
  }

  /** Advance the arc. Devotion will call this; for now the player's keys do. */
  grow(by: number): void {
    this.reach = clamp01(this.reach + by);
  }

  toSnapshot(): DeitySnapshot {
    return {
      id: this.id, kind: this.kind, reach: this.reach,
      power: this.power, impulse: this.impulse, spent: this.spent,
      ...(this.seat ? { seat: { ...this.seat } } : {}),
    };
  }

  static fromSnapshot(s: DeitySnapshot | { reach: number } | undefined): GodState {
    if (!s) return new GodState(0);
    const d = s as Partial<DeitySnapshot>;
    return new GodState(s.reach ?? 0, {
      id: d.id, kind: d.kind, power: d.power, impulse: d.impulse, spent: d.spent, seat: d.seat,
    });
  }
}

/**
 * Every god in the world (v0.3.2 §deity identity).
 *
 * The player is one entry, not a special case: rival gods act through the
 * same verbs, emit the same events, and are witnessed by the same eyes. That
 * is the whole design — a pantheon with no privileged member is the only way
 * "can I tell another god has been here?" can ever be an honest question.
 * The registry is the world's own bookkeeping and lives in the simulation,
 * so a save restores who was acting as well as what they did.
 */
export class Pantheon {
  private readonly gods = new Map<string, GodState>();

  constructor(...gods: GodState[]) {
    for (const g of gods) this.gods.set(g.id, g);
    if (!this.gods.has('player')) {
      const p = new GodState(0);
      this.gods.set(p.id, p);
    }
  }

  /** The deity these hands belong to. Always present. */
  get player(): GodState {
    return this.gods.get('player')!;
  }

  get(id: string): GodState | undefined {
    return this.gods.get(id);
  }

  /** Every god, in insertion order — the world's view, never a mortal's. */
  all(): readonly GodState[] {
    return [...this.gods.values()];
  }

  /** Rivals only: everyone whose hands are not the player's. */
  rivals(): readonly GodState[] {
    return this.all().filter((g) => g.kind !== 'player');
  }

  add(g: GodState): GodState {
    this.gods.set(g.id, g);
    return g;
  }

  /** Replace the roll in place — a restore keeps the Simulation's own object. */
  restore(s: DeitySnapshot[]): void {
    this.gods.clear();
    for (const d of s) this.gods.set(d.id, GodState.fromSnapshot(d));
    if (!this.gods.has('player')) this.gods.set('player', new GodState(0));
  }

  toSnapshot(): DeitySnapshot[] {
    return this.all().map((g) => g.toSnapshot());
  }

  static fromSnapshot(s: DeitySnapshot[] | undefined): Pantheon {
    return new Pantheon(...(s ?? []).map((d) => GodState.fromSnapshot(d)));
  }
}

/** Every rung, for a HUD that wants to show the arc rather than one point. */
export const godLadder = (): readonly GodStageInfo[] => LADDER;

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
